import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Popper } from '@mui/material';
import { FindLoad, FindSuccessItem, Images, useModal } from 'utils';
import { CloseButton, MinLoader, ToolInfo } from 'components';
import { clearinghouseStatusList } from '../constants';
import { claimActions } from 'store';
import { RejectClaim } from './rejectClaim';
import { claimReasonList } from './constnats';

const ACTION_TYPE = 'CHANGE_CLAIM_STATUS';

const STATUS_FILTERS = {
    green: 'brightness(0) saturate(100%) invert(34%) sepia(82%) saturate(946%) hue-rotate(117deg) brightness(91%) contrast(98%)',
    red: 'brightness(0) saturate(100%) invert(31%) sepia(51%) saturate(3011%) hue-rotate(343deg) brightness(100%) contrast(89%)',
    amber: 'brightness(0) saturate(100%) invert(58%) sepia(35%) saturate(1632%) hue-rotate(358deg) brightness(99%) contrast(95%)',
    none: '',
};

const STATUS_CONFIG = {
    IN_CH: {
        text: 'In Clearinghouse',
        color: '#175CD3',
        circleColor: '#175CD3',
        backgroundColor: '#EFF8FF',
        filter: STATUS_FILTERS.none,
        value: 'IN_CH',
    },
    CH_REJECTED: {
        text: 'Clearinghouse Rejected',
        color: '#B42318',
        circleColor: '#B42318',
        backgroundColor: '#FEF3F2',
        filter: STATUS_FILTERS.red,
        value: 'CH_REJECTED',
    },
    NOT_DELIVERED: {
        text: 'Not Delivered',
        color: '#4B5C68',
        circleColor: '#4B5C68',
        backgroundColor: '#F2F4F7',
        filter: STATUS_FILTERS.none,
        value: 'NOT_DELIVERED',
    },
    PAYER_PRECHECK: {
        text: 'Payer Precheck',
        color: '#B54708',
        circleColor: '#B54708',
        backgroundColor: '#FFFAEB',
        filter: STATUS_FILTERS.amber,
        value: 'PAYER_PRECHECK',
    },
    ACCEPTED_BY_PAYER: {
        text: 'Accepted by Payer',
        color: '#027A48',
        circleColor: '#027A48',
        backgroundColor: '#ECFDF3',
        filter: STATUS_FILTERS.green,
        value: 'ACCEPTED_BY_PAYER',
    },
    PARTIAL_PMT: {
        text: 'Partial Payment',
        color: '#B93817',
        circleColor: '#D5431D',
        backgroundColor: '#FDD0A3',
        filter: STATUS_FILTERS.red,
        value: 'PARTIAL_PMT',
    },
    DENIED_BY_PAYER: {
        text: 'Denied by Payer',
        color: '#912018',
        circleColor: '#912018',
        backgroundColor: '#FEE4E2',
        filter: STATUS_FILTERS.red,
        value: 'DENIED_BY_PAYER',
    },
};

const LEGACY_STATUS_MAP = {
    PENDING: 'IN_CH',
    SUBMITTED: 'IN_CH',
    ACCEPTED: 'IN_CH',
    REJECTED: 'CH_REJECTED',
    PENDING_PAYER: 'PAYER_PRECHECK',
    ACCEPTED_PAYER: 'ACCEPTED_BY_PAYER',
    REJECTED_PAYER: 'DENIED_BY_PAYER',
    NOT_DELIVERED: 'NOT_DELIVERED',
};

const REJECTION_STATUSES = ['CH_REJECTED', 'DENIED_BY_PAYER'];

const renderColorAndText = (status) => {
    const normalized = LEGACY_STATUS_MAP[status] || status;
    return STATUS_CONFIG[normalized] || { backgroundColor: '', circleColor: '', color: '', text: '', filter: '' };
};
export const SubmittedClaimStatus = ({ info, single, noAction, afterSuccess }) => {
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const openStatus = Boolean(anchorEl);
    const id = openStatus ? 'simple-popper' : undefined;
    const [status, setStatus] = useState('');
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const { open } = useModal();
    const { filter, backgroundColor, circleColor, color, text } = renderColorAndText(info?.clearinghouseStatus);
    const statuses = clearinghouseStatusList.map((s) => ({ ...renderColorAndText(s?.id) }));

    useEffect(() => {
        if (success && afterSuccess) {
            afterSuccess();
        }
    }, [success]);

    const handleClick = (event) => {
        if (!noAction) {
            event.preventDefault();
            event.stopPropagation();
            setAnchorEl(anchorEl ? null : event.currentTarget);
        }
    };

    const handleChangeStatus = (type) => {
        setAnchorEl(null);
        if (REJECTION_STATUSES.includes(type?.value)) {
            open(<RejectClaim ids={[info?.id]} single={single} status={type?.value} />);
        } else {
            setStatus(type?.value);
            dispatch(
                claimActions.changeClaimStatus(
                    {
                        claimIds: [info?.id],
                        clearinghouseStatus: type?.value,
                    },
                    single
                )
            );
        }
    };

    return (
        <div>
            <div className="flex-align-center" style={{ gap: 8 }}>
                <button
                    aria-describedby={id}
                    onClick={handleClick}
                    className="status-card-box"
                    style={{ backgroundColor: backgroundColor, border: 'none', height: 28 }}
                >
                    {text && <div style={{ backgroundColor: circleColor }} />}
                    <p style={{ color: color }}>{text ? text : '--'}</p>
                    {!noAction && <img style={{ filter: filter }} src={Images.dropdownArrow} alt="" />}
                </button>

                {[...REJECTION_STATUSES, LEGACY_STATUS_MAP.NOT_DELIVERED].includes(info?.clearinghouseStatus) &&
                    (info?.clearinghouseReason) && (
                        <div style={{ marginTop: 2 }}>
                            <ToolInfo text={`Reason: ${info?.clearinghouseReason}`}/>
                        </div>
                    )}
            </div>

            <Popper
                id={id}
                open={openStatus}
                anchorEl={anchorEl}
                className="submitted-claim-status-box"
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 1500 }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <div>
                    <div className="submitted-claim-status-header">
                        <p>Update Status</p>
                        <CloseButton handleCLic={handleClick} />
                    </div>
                    <div className="submitted-claim-status-body">
                        {statuses?.map((i, j) => (
                            <button
                                key={j}
                                onClick={() => handleChangeStatus(i)}
                                className="status-card-box"
                                style={{ backgroundColor: 'transparent', border: 'none' }}
                            >
                                {status === i?.value && loader?.length ? (
                                    <MinLoader height={8} width={8} color={i?.circleColor} margin={'0'} position={'relative'} />
                                ) : (
                                    <div style={{ backgroundColor: i?.circleColor }} />
                                )}
                                <p style={{ color: i?.color }}>{i?.text}</p>
                            </button>
                        ))}
                    </div>
                </div>
            </Popper>
            {openStatus && (
                <div
                    className="beck-drop"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAnchorEl(null);
                    }}
                />
            )}
        </div>
    );
};
